import { type ComponentProps, type ComponentType, type ElementType,forwardRef } from 'react';
import { styled } from '@mui/material';

/** The two text directions the app supports. */
export type NoFlipMode = 'ltr' | 'rtl';

/** Props every NoFlip component accepts on top of the wrapped component's own. */
export interface NoFlipOwnProps {
  /**
   * Direction to pin on the subtree. Defaults to `ltr`.
   *
   * Pass `useDirection().dir` when the subtree should simply follow the app
   * language; pass a literal `"ltr"` when the content is direction-neutral and
   * must always read left-to-right (file names, emails, URLs, code, numbers,
   * charts).
   */
  mode?: NoFlipMode;
}

export type NoFlipComponent<P> = ComponentType<P & NoFlipOwnProps & { component?: ElementType }>;

/**
 * Wraps a component so its subtree direction is pinned and immune to RTL
 * rewriting.
 *
 * This is the single sanctioned way to control text direction in the web app.
 * Writing `direction` in `sx`/`style` does NOT work: the app renders through an
 * Emotion cache with `@mui/stylis-plugin-rtl`, and cssjanus swaps every
 * `direction: ltr` for `direction: rtl` and vice versa — so a hand-written
 * `direction: 'rtl'` silently becomes `ltr` in Arabic, the exact opposite of
 * what was intended. The `/* @noflip *\/` marker below is cssjanus's escape
 * hatch and keeps the declaration verbatim.
 *
 * The wrapper also mirrors `mode` onto the DOM `dir` attribute, so the bidi
 * algorithm isolates the subtree the same way a hand-written `dir` did. That
 * makes it a strict superset of both patterns it replaces.
 *
 * Call this at module scope — never inside a component body, or every render
 * creates a new styled component and remounts the subtree.
 *
 * @example
 * const NoFlipCard = createNoFlip(Card);
 *
 * const MyPanel = () => {
 *   const { dir } = useDirection();
 *   return <NoFlipCard mode={dir}>…</NoFlipCard>;
 * };
 */
// Constrained to `ElementType` rather than `ComponentType<…>`: parameter
// contravariance makes the narrower forms reject any component that has a
// required prop (Menu's `open`, for one), and `ElementType` is also exactly what
// `ComponentProps<T>` needs to infer the real prop type.
export function createNoFlip<T extends ElementType>(
  component: T,
): NoFlipComponent<ComponentProps<T>> {
  const StyledComponent = styled(component as ComponentType<Record<string, unknown>>, {
    shouldForwardProp: (prop) => prop !== 'mode',
  })<NoFlipOwnProps>`
    /* @noflip */
    direction: ${({ mode }) => mode ?? 'ltr'};
  `;

  const Styled = StyledComponent as unknown as ComponentType<Record<string, unknown>>;

  // Deliberately not a mobx `observer`: this reads no observable state of its
  // own, and wrapping it would turn every NoFlip subtree into a reaction
  // boundary. Call sites stay observers, and so does the component being wrapped.
  // eslint-disable-next-line mobx/missing-observer
  const NoFlip = forwardRef<unknown, ComponentProps<T> & NoFlipOwnProps>(function NoFlip(
    { mode = 'ltr', ...rest },
    ref,
  ) {
    // `dir` before the spread so an explicit `dir` at the call site still wins.
    return <Styled ref={ref} mode={mode} dir={mode} {...rest} />;
  });

  const wrapped = component as { displayName?: string; name?: string };
  NoFlip.displayName = `NoFlip(${wrapped.displayName ?? wrapped.name ?? 'Component'})`;

  return NoFlip as unknown as NoFlipComponent<ComponentProps<T>>;
}
